import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { firstValueFrom, type Observable, take } from 'rxjs';

import { I18nService } from '@core/i18n-legacy';
import { TellentCurrentUserDataFacade, TellentMembersDataFacade } from '@core/tellent-logic';
import {
  BulkUpdateMembersPayload,
  Seat,
  SeatKind,
  TellentMember,
  UserInvitationFlowData,
} from '@core/tellent-types';
import { DialogRef, DialogService, DialogWidth, RtPlatform } from '@core/user-interface-legacy';

import { ChangeLoginMethodDialogComponent } from './change-login-method-dialog/change-login-method-dialog.component';
import { ResetPasswordConfirmDialogComponent } from './reset-password-confirm-dialog/reset-password-confirm-dialog.component';
import { AddEmployeesToListDialog } from '../../add-employess-to-list-dialog/add-employees-to-list-dialog.component';
import { DownloadCredentialsDialogComponent } from '../../download-credentials-dialog/download-credentials-dialog.component';
import { MemberAddedSuccessDialogComponent } from '../../member-added-success-dialog/member-added-success-dialog.component';
import { UserInvitationFlowFacade } from '../../services/user-invitation-flow.facade';
import { SubdomainInformationDialogComponent } from '../../subdomain-information-dialog/subdomain-information-dialog.component';

@Injectable({ providedIn: 'root' })
export class EditMembersActionsService {
  private readonly dialogService = inject(DialogService);
  private readonly i18n = inject(I18nService);
  private readonly platform = inject(RtPlatform);
  private readonly membersDataFacade = inject(TellentMembersDataFacade);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentUserFacade = inject(TellentCurrentUserDataFacade);
  private readonly userInvitationFlowFacade = inject(UserInvitationFlowFacade);

  private readonly coreHrSeat = this.currentUserFacade.seatByKind(SeatKind.CoreHR);

  public startInviteExistingMemberFlow(dialogRef: DialogRef, memberGuid: string): void {
    this.userInvitationFlowFacade.startInviteExistingMemberFlow(dialogRef, memberGuid);
  }

  public sendInvitation(memberGuid: string): void {
    this.membersDataFacade.sendInvitation(memberGuid);
  }

  public sendPasswordResetEmail(userGuid: string): void {
    this.membersDataFacade.sendPasswordReset(userGuid);
  }

  public async resetPassword(memberData: TellentMember, fullName: string, dialogRef: DialogRef) {
    const { afterClosed } = this.dialogService.openCustom(
      ResetPasswordConfirmDialogComponent,
      {
        width: DialogWidth.SmallX,
      },
      { fullName: fullName },
    );
    const result = await firstValueFrom(afterClosed);

    if (!result) {
      return;
    }

    if (result) {
      dialogRef.close();
      await this.openDownloadCredentialsDialog(memberData, dialogRef);
    }
  }

  public startPreonboarding(dialogRef: DialogRef, memberGuid: string): void {
    this.userInvitationFlowFacade.startInviteExistingMemberForPreonboardingFlow(
      dialogRef,
      memberGuid,
    );
  }

  public async addEmployeeToListWithoutAccess(members: TellentMember[]): Promise<void> {
    const { afterClosed } = this.dialogService.openDialog(
      AddEmployeesToListDialog,
      { members },
      {
        width: DialogWidth.SmallX,
      },
    );

    const result = await firstValueFrom(afterClosed);

    if (!result?.confirmed) {
      return;
    }

    const { access, appGuid } = <Seat>this.coreHrSeat();

    const payload: BulkUpdateMembersPayload = {
      members: members.map(member => ({
        guid: member.guid,
        email: member.email,
        personalEmail: member.personalEmail,
      })),
      seats: [
        {
          appGuid,
          access,
          roleExternalId: '',
        },
      ],
      abilities: [],
      generateLogin: false,
    };

    this.membersDataFacade.inviteExistingMember(payload);

    this.membersDataFacade.inviteExistingMemberSuccess$
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const dialogData: UserInvitationFlowData = {
          payload: members,
          inviteMethod: null,
        };

        this.dialogService.openDialog(MemberAddedSuccessDialogComponent, dialogData, {
          width: DialogWidth.SmallX,
        });
      });
  }

  public changeLoginMethod(login: string, memberGuid: string) {
    this.dialogService.openDialog(
      ChangeLoginMethodDialogComponent,
      { username: login, memberGuid: memberGuid },
      {
        width: DialogWidth.SmallX,
      },
    );
  }

  public pauseAccess() {
    // noop, for later
  }

  public inviteExistingMember(payload: BulkUpdateMembersPayload): void {
    this.membersDataFacade.inviteExistingMember(payload);
  }

  public async reactivateMember(
    memberGuid: string,
    fullName: string,
    seats: Seat[],
  ): Promise<void> {
    const confirmed = await firstValueFrom(this.openReactivateConfirmationDialog(fullName, seats));
    if (confirmed) {
      this.membersDataFacade.reactivateMember(memberGuid);
    }
  }

  public async deactivateMember(memberGuid: string, fullName: string): Promise<void> {
    const confirmed = await firstValueFrom(this.deactivateAccountDialog(fullName));
    if (confirmed) {
      this.membersDataFacade.deactivateMember(memberGuid);
    }
  }

  public async deleteMember(memberGuid: string, fullName: string): Promise<void> {
    const confirmed = await firstValueFrom(this.deleteUserAccountDialog(fullName));
    if (confirmed) {
      this.membersDataFacade.deleteMember(memberGuid);
    }
  }

  public async openDownloadCredentialsDialog(
    memberData: TellentMember,
    dialogRef?: DialogRef,
  ): Promise<boolean> {
    const dialogData: UserInvitationFlowData = { payload: [memberData] };
    const { afterClosed } = this.dialogService.openDialog(
      DownloadCredentialsDialogComponent,
      dialogData,
      {
        width: DialogWidth.SmallX,
      },
    );
    const result = await firstValueFrom(afterClosed);

    if (result && dialogRef) {
      await this.openSubdomainInformationDialog(memberData);
      dialogRef.close();
    }

    return !!result;
  }

  public async openSubdomainInformationDialog(memberData: TellentMember): Promise<void> {
    const dialogData: UserInvitationFlowData = { payload: [memberData] };
    const { afterClosed } = this.dialogService.openDialog(
      SubdomainInformationDialogComponent,
      dialogData,
      {
        width: DialogWidth.SmallX,
      },
    );
    await firstValueFrom(afterClosed);
  }

  private openReactivateConfirmationDialog(fullName: string, seats: Seat[]): Observable<boolean> {
    return this.dialogService.information({
      title: this.i18n.translate('tlc.members.edit_member.actions.confirm_dialog.title', {
        member: fullName,
      }),
      message: this.i18n.translate('tlc.members.edit_member.actions.confirm_dialog.message', {
        member: fullName,
        recruitment: this.findSeatRole(seats, SeatKind.Recruitment),
        coreHR: this.findSeatRole(seats, SeatKind.CoreHR),
      }),
      agreeLabel: this.i18n.translate('tlc.members.edit_member.actions.confirm_dialog.agreeLabel'),
      mobileOptions: this.platform.IS_MOBILE
        ? {
            mobileModal: true,
          }
        : undefined,
    });
  }

  private findSeatRole(seats: Seat[], kind: SeatKind): string {
    const roleName = seats.find(seat => seat.app.kind === kind)?.role?.name;
    return roleName ? roleName : this.i18n.translate('tlc.common.no_access');
  }

  private deactivateAccountDialog(fullName: string): Observable<boolean> {
    return this.dialogService.warning({
      title: this.i18n.translate('tlc.members.edit_member.deactivate_dialog.title', {
        member: fullName,
      }),
      message: this.i18n.translate('tlc.members.edit_member.deactivate_dialog.message', {
        member: fullName,
      }),
      agreeLabel: this.i18n.translate('tlc.members.edit_member.deactivate_dialog.agreeLabel'),
      mobileOptions: this.platform.IS_MOBILE
        ? {
            mobileModal: true,
          }
        : undefined,
    });
  }

  private deleteUserAccountDialog(fullName: string): Observable<boolean> {
    return this.dialogService.error({
      title: this.i18n.translate('tlc.members.edit_member.delete_dialog.title', {
        member: fullName,
      }),
      message: this.i18n.translate('tlc.members.edit_member.delete_dialog.message', {
        member: fullName,
      }),
      agreeLabel: this.i18n.translate('tlc.members.edit_member.delete_dialog.agreeLabel'),
      mobileOptions: this.platform.IS_MOBILE
        ? {
            mobileModal: true,
          }
        : undefined,
    });
  }
}
